import {
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Title
} from '@mantine/core'
import { FormattedMessage } from 'react-intl'
import { GpuLoadCharts } from '@/components/deployments/deployment-details/GpuLoadCharts'
import {
  TIME_RANGE_OPTIONS,
  useMetricTimeRange
} from '@/components/deployments/deployment-details/metricTimeRanges'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetDeviceGroups } from '@/queries/deploymentQueries'
import { Device } from '@/types/device'
import { CrudAction, Resource } from '@/types/permissions'
import { getDeviceGpus } from '@/utils/device/getDeviceAttr'

type DeviceGpuChartsProps = {
  device: Device
}

export const DeviceGpuCharts = ({ device }: DeviceGpuChartsProps) => {
  const gpus = getDeviceGpus(device)
  const { range, startTs, endTs, selectRange } = useMetricTimeRange()
  const [canReadDeployments] = useHasPermissions(Resource.Deployments, [
    CrudAction.Read
  ])

  const { data: groups } = useGetDeviceGroups(
    device.id,
    canReadDeployments && gpus.length > 0
  )

  if (gpus.length === 0) {
    return null
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={5}>
              <FormattedMessage id="devices.gpuLoad.title" />
            </Title>
            <Text size="xs" c="dimmed">
              <FormattedMessage id="devices.gpuLoad.description" />
            </Text>
          </Stack>

          <SegmentedControl
            data={TIME_RANGE_OPTIONS}
            value={range.label}
            w={200}
            size="xs"
            color="brand-primary"
            onChange={selectRange}
          />
        </Group>

        {gpus.map((gpu) => (
          <GpuLoadCharts
            key={gpu.index}
            deviceId={device.id}
            gpu={gpu}
            groups={groups}
            range={range}
            startTs={startTs}
            endTs={endTs}
          />
        ))}
      </Stack>
    </Paper>
  )
}
